//
//  WebsiteEmbedTool.tsx
//
//  Lives under My Trails → Tools → Website embed.
//
//  Lets a trail admin drop a live status badge into their club's
//  website. Two delivery methods, four layouts, full co-creation
//  via CSS theming. Admin-pushed status only — cron classifications
//  are excluded by design (that's the moat vs. Trailbot, whose embed
//  shows "Updated 4 months ago" because their feed is whatever last
//  classified).
//
//  Top-level structure:
//
//    [Layout chooser]    Card · Inline · Badge · Aggregator (all trails)
//    [Live preview]      Real iframe of the chosen layout
//    [Delivery tabs]     Recommended (web component) / Universal (iframe)
//    [Embed code]        Mono textarea + Copy
//    [Co-create guide]   CSS custom properties + ::part() recipe
//
//  Why no "theme" picker on the host page anymore: the web component
//  inherits color from the host site's `color` (via currentColor), and
//  the iframe falls through to `auto` which respects the visitor's
//  system preference. Removing the toggle removes a fake choice —
//  Hick's Law improvement.
//
//  Laws of UX honored:
//   • Aesthetic-Usability   Each layout is genuinely distinct + worth
//                           embedding proudly.
//   • Doherty Threshold     Preview reloads instantly on layout
//                           change (no network for the embed config).
//   • Jakob's Law           Tabs + Copy mirrors every embed UI on
//                           the web.
//   • Hick's Law            4 layouts × 2 delivery methods = 8
//                           snippets, all auto-generated from one
//                           layout pick. No 20-knob dashboards.
//   • Goal-Gradient         Embed code visible the moment a layout
//                           is selected.
//   • Peak-End Rule         Copy button transforms to "Copied" on
//                           success.
//   • Von Restorff          Olive "Live" badge anchors the eye to
//                           the moat-defining feature.
//

import { useEffect, useMemo, useRef, useState } from "react";
import { type AdminAssignment } from "../api";

type Layout = "card" | "inline" | "badge" | "org";
type Delivery = "component" | "iframe";

interface WebsiteEmbedToolProps {
  trail: AdminAssignment;
}

function embedOrigin(): string {
  if (typeof window === "undefined") {
    return "https://trail-status-poller.trailstatuspro.workers.dev";
  }
  if (
    window.location.host.includes("workers.dev") ||
    window.location.host.includes("trailstatuspro.com")
  ) {
    return window.location.origin;
  }
  return "https://trail-status-poller.trailstatuspro.workers.dev";
}

export function WebsiteEmbedTool({ trail }: WebsiteEmbedToolProps) {
  const [layout, setLayout] = useState<Layout>("card");
  const [delivery, setDelivery] = useState<Delivery>("component");
  const [copied, setCopied] = useState(false);

  const origin = useMemo(embedOrigin, []);
  const orgSlug = trail.organization?.slug ?? null;

  const previewUrl = useMemo(() => {
    if (layout === "org" && orgSlug) {
      return `${origin}/embed/org/${orgSlug}`;
    }
    const u = new URL(`/embed/${trail.slug}`, origin);
    if (layout !== "card") u.searchParams.set("layout", layout);
    return u.toString();
  }, [origin, trail.slug, layout, orgSlug]);

  const embedSnippet = useMemo(
    () =>
      generateSnippet({
        delivery,
        layout,
        trailSlug: trail.slug,
        trailName: trail.name,
        orgSlug,
        orgName: trail.organization?.name ?? null,
        origin,
      }),
    [delivery, layout, trail.slug, trail.name, orgSlug, trail.organization?.name, origin]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied(true);
    } catch {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    }
  };

  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2200);
    return () => window.clearTimeout(id);
  }, [copied]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewFrameRef = useRef<HTMLIFrameElement>(null);

  // Auto-resize the preview iframe via the postMessage protocol the
  // /embed/:slug page emits. The visitor's iframe on a trail club
  // site benefits from the same handler — we surface it here so the
  // admin sees the final size their visitors will get.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== previewFrameRef.current?.contentWindow) return;
      const d = e.data as unknown;
      if (
        d &&
        typeof d === "object" &&
        (d as { type?: string }).type === "tsp-resize"
      ) {
        const h = Math.min(
          720,
          Math.max(40, Number((d as { height?: number }).height) || 0)
        );
        if (previewFrameRef.current) {
          previewFrameRef.current.style.height = `${h}px`;
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const orgAvailable = !!orgSlug;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {/* CARD 1 — Layout chooser + Live preview */}
      <section className="card">
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              Layout
            </h3>
            <p
              style={{
                margin: "3px 0 0",
                fontSize: 13,
                color: "var(--text-dim)",
              }}
            >
              Pick a shape. Preview updates live — it&apos;s a real iframe of
              what your visitors will see.
            </p>
          </div>
          <LiveBadge />
        </header>

        <LayoutPicker
          value={layout}
          onChange={setLayout}
          orgAvailable={orgAvailable}
          orgName={trail.organization?.name ?? null}
        />

        <div
          style={{
            marginTop: 18,
            border: "1px solid var(--border)",
            borderRadius: 10,
            padding: 18,
            backgroundColor: "var(--bg)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <iframe
            ref={previewFrameRef}
            key={previewUrl}
            src={previewUrl}
            title="Widget preview"
            width="100%"
            height={layout === "badge" ? 40 : layout === "inline" ? 56 : 200}
            style={{
              border: 0,
              maxWidth: layout === "org" ? 560 : 480,
              display: "block",
              transition: "height 200ms ease",
            }}
          />
        </div>
      </section>

      {/* CARD 2 — Delivery + Embed code */}
      <section className="card">
        <header style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Embed code
          </h3>
          <p
            style={{
              margin: "3px 0 0",
              fontSize: 13,
              color: "var(--text-dim)",
            }}
          >
            Two delivery methods. Recommended works on most modern sites.
            Universal works everywhere — including Wix and Squarespace
            free tier.
          </p>
        </header>

        <Tabs
          value={delivery}
          onChange={setDelivery}
          options={[
            { id: "component", label: "Recommended", subtitle: "Inherits your site's design" },
            { id: "iframe", label: "Universal", subtitle: "Works on any site" },
          ]}
        />

        <DeliveryNote delivery={delivery} />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 14,
            marginBottom: 8,
            gap: 12,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: "var(--text-dim)",
            }}
          >
            Paste this on your site
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className={copied ? "btn primary" : "btn"}
            style={{ minWidth: 110, justifyContent: "center" }}
            aria-live="polite"
          >
            {copied ? "Copied" : "Copy code"}
          </button>
        </div>

        <textarea
          ref={textareaRef}
          readOnly
          value={embedSnippet}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight: 200,
            fontFamily: "var(--mono)",
            fontSize: 13,
            lineHeight: 1.55,
            padding: 14,
            backgroundColor: "var(--bg)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            resize: "vertical",
            tabSize: 2,
          }}
          onFocus={(e) => e.target.select()}
        />
      </section>

      {/* CARD 3 — Co-create theming guide (only shown for the web component) */}
      {delivery === "component" && (
        <section className="card">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Match your site&apos;s design
          </h3>
          <p
            style={{
              margin: "4px 0 14px",
              fontSize: 13,
              color: "var(--text-dim)",
              lineHeight: 1.55,
            }}
          >
            The widget inherits your site&apos;s font and text color
            automatically. To go further, drop these CSS variables in
            your stylesheet — or use the <code>::part()</code> selectors
            for deeper control. Your fonts, your colors, our data.
          </p>

          <ThemingExample />
        </section>
      )}

      {/* CARD 4 — Why ours is different */}
      <section className="card">
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
          Why this is different
        </h3>
        <ul
          style={{
            margin: "12px 0 0",
            paddingLeft: 20,
            display: "grid",
            gap: 8,
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--text-dim)",
          }}
        >
          <li>
            <strong style={{ color: "var(--text)" }}>Real-time, not cron-stale.</strong>{" "}
            The widget reflects what you push the moment you push it. No
            stale &quot;Updated 4 months ago&quot; footers.
          </li>
          <li>
            <strong style={{ color: "var(--text)" }}>Your status, your voice.</strong>{" "}
            The summary text is exactly what you typed in Notifications —
            the page reads in your words, not a machine&apos;s.
          </li>
          <li>
            <strong style={{ color: "var(--text)" }}>
              Co-creates with your site.
            </strong>{" "}
            The Recommended embed inherits your fonts, colors, and
            spacing. We provide the data and a tasteful default — your
            site stays in charge of the look.
          </li>
          <li>
            <strong style={{ color: "var(--text)" }}>
              No tracking, no cookies.
            </strong>{" "}
            Read-only badge. Your visitors give us nothing.
          </li>
        </ul>
      </section>
    </div>
  );
}

// ====================================================================
//  Snippet generator
// ====================================================================

interface SnippetArgs {
  delivery: Delivery;
  layout: Layout;
  trailSlug: string;
  trailName: string;
  orgSlug: string | null;
  orgName: string | null;
  origin: string;
}

function generateSnippet(a: SnippetArgs): string {
  if (a.delivery === "component") return componentSnippet(a);
  return iframeSnippet(a);
}

function componentSnippet(a: SnippetArgs): string {
  const scriptTag = `<!-- Loaded once per page; safe to include on multiple widgets -->
<script src="${a.origin}/widget.js" defer></script>`;

  if (a.layout === "org" && a.orgSlug) {
    return `${scriptTag}

<!-- Trail Status Pro — live status for ${escapeHtmlComment(a.orgName ?? "your org")} -->
<trail-status-pro org-slug="${a.orgSlug}"></trail-status-pro>`;
  }

  return `${scriptTag}

<!-- Trail Status Pro — live status for ${escapeHtmlComment(a.trailName)} -->
<trail-status-pro
  slug="${a.trailSlug}"
  layout="${a.layout}"
></trail-status-pro>`;
}

function iframeSnippet(a: SnippetArgs): string {
  const src =
    a.layout === "org" && a.orgSlug
      ? `${a.origin}/embed/org/${a.orgSlug}`
      : a.layout === "card"
      ? `${a.origin}/embed/${a.trailSlug}`
      : `${a.origin}/embed/${a.trailSlug}?layout=${a.layout}`;
  const initialHeight =
    a.layout === "badge"
      ? 40
      : a.layout === "inline"
      ? 56
      : a.layout === "org"
      ? 240
      : 200;
  const title = a.layout === "org" ? a.orgName ?? "Trail Status" : a.trailName;

  return `<!-- Trail Status Pro — live status for ${escapeHtmlComment(title)} -->
<iframe
  src="${src}"
  title="${escapeAttr(title)} — live trail status"
  id="tsp-${a.trailSlug}"
  width="100%"
  height="${initialHeight}"
  frameborder="0"
  loading="lazy"
  style="border: 0; max-width: ${a.layout === "org" ? 560 : 480}px; display: block;"
></iframe>

<!-- Auto-resize so there's no internal scrollbar. Drop once per page. -->
<script>
window.addEventListener("message", function (e) {
  if (!e.data || e.data.type !== "tsp-resize") return;
  var frames = document.querySelectorAll("iframe[id^='tsp-']");
  for (var i = 0; i < frames.length; i++) {
    if (frames[i].contentWindow === e.source) {
      frames[i].style.height = Math.max(40, e.data.height) + "px";
    }
  }
});
</script>`;
}

function escapeHtmlComment(s: string): string {
  return s.replace(/--/g, "—");
}
function escapeAttr(s: string): string {
  return s.replace(/"/g, "&quot;");
}

// ====================================================================
//  Sub-components
// ====================================================================

function LiveBadge() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: "var(--olive)",
        border: "1px solid var(--olive)",
        backgroundColor: "rgba(196, 180, 84, 0.08)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "var(--olive)",
          boxShadow: "0 0 0 4px rgba(196, 180, 84, 0.18)",
        }}
      />
      Live
    </span>
  );
}

interface LayoutPickerProps {
  value: Layout;
  onChange: (v: Layout) => void;
  orgAvailable: boolean;
  orgName: string | null;
}

function LayoutPicker({ value, onChange, orgAvailable, orgName }: LayoutPickerProps) {
  const options: { id: Layout; label: string; sub: string; disabled?: boolean }[] = [
    { id: "card", label: "Card", sub: "Hero placement on a trail page" },
    { id: "inline", label: "Inline strip", sub: "Sidebar, header bar, footer" },
    { id: "badge", label: "Badge", sub: "Inline with text or in a table" },
    {
      id: "org",
      label: "Aggregator",
      sub: orgAvailable
        ? `All trails in ${orgName ?? "your org"}`
        : "No organization linked",
      disabled: !orgAvailable,
    },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Widget layout"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 10,
      }}
    >
      {options.map((opt) => {
        const active = opt.id === value;
        const disabled = opt.disabled === true;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            style={{
              textAlign: "left",
              padding: "12px 14px",
              borderRadius: 10,
              border: active
                ? "1px solid var(--olive)"
                : "1px solid var(--border)",
              backgroundColor: active
                ? "rgba(196, 180, 84, 0.10)"
                : "var(--surface-2)",
              color: disabled ? "var(--text-mute)" : "var(--text)",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              transition:
                "background-color 140ms ease, border-color 140ms ease",
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{opt.label}</div>
            <div
              style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}
            >
              {opt.sub}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface TabsProps {
  value: Delivery;
  onChange: (v: Delivery) => void;
  options: { id: Delivery; label: string; subtitle: string }[];
}

function Tabs({ value, onChange, options }: TabsProps) {
  return (
    <div
      role="tablist"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        gap: 8,
      }}
    >
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(opt.id)}
            style={{
              textAlign: "left",
              padding: "12px 14px",
              border: active
                ? "1px solid var(--olive)"
                : "1px solid var(--border)",
              borderRadius: 10,
              backgroundColor: active
                ? "rgba(196, 180, 84, 0.10)"
                : "var(--surface-2)",
              color: "var(--text)",
              cursor: "pointer",
              transition: "background-color 140ms ease, border-color 140ms ease",
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{opt.label}</div>
            <div
              style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}
            >
              {opt.subtitle}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DeliveryNote({ delivery }: { delivery: Delivery }) {
  if (delivery === "component") {
    return (
      <p
        style={{
          margin: "12px 0 0",
          fontSize: 12.5,
          color: "var(--text-dim)",
          lineHeight: 1.55,
        }}
      >
        Works on Squarespace Business+, Wordpress.org, Webflow, Ghost, and
        plain HTML — anywhere a custom <code>&lt;script&gt;</code> tag is
        allowed. Visually inherits your site&apos;s fonts and text color
        automatically.
      </p>
    );
  }
  return (
    <p
      style={{
        margin: "12px 0 0",
        fontSize: 12.5,
        color: "var(--text-dim)",
        lineHeight: 1.55,
      }}
    >
      Works on every CMS — including Wix, Squarespace free tier,
      Wordpress.com free, and AMP pages. The widget auto-sizes its iframe
      via <code>postMessage</code>, so no internal scrollbar on mobile.
    </p>
  );
}

function ThemingExample() {
  const recipe = `/* Drop this in your site's stylesheet to co-create
   with the widget's design. All variables are optional. */
trail-status-pro {
  /* Typography (inherits your site by default) */
  --tsp-font-family: inherit;
  --tsp-name-size: 16px;
  --tsp-name-weight: 600;

  /* Container */
  --tsp-radius: 12px;
  --tsp-padding: 14px 16px;
  --tsp-bg: transparent;
  --tsp-border-color: currentColor;

  /* Status colors (override to match your brand) */
  --tsp-accent-open: #2EA46B;
  --tsp-accent-closed: #D73F3F;
  --tsp-accent-partially_open: #C99518;
  --tsp-accent-unknown: #686659;
}

/* For deeper control, target Shadow DOM parts */
trail-status-pro::part(name)    { font-family: "Lato", sans-serif; }
trail-status-pro::part(pill)    { letter-spacing: 0.02em; }
trail-status-pro::part(summary) { font-style: italic; }`;
  return (
    <pre
      style={{
        margin: 0,
        padding: 14,
        backgroundColor: "var(--bg)",
        color: "var(--text)",
        border: "1px solid var(--border)",
        borderRadius: 8,
        fontFamily: "var(--mono)",
        fontSize: 12.5,
        lineHeight: 1.55,
        whiteSpace: "pre-wrap",
        overflow: "auto",
      }}
    >
      {recipe}
    </pre>
  );
}
